package com.devi3east.bashbuddy

import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.window.ComposeViewport
import androidx.navigation.compose.rememberNavController
import androidx.savedstate.read
import com.devi3east.bashbuddy.cart.navigation.STRIPE_ORG_ID_ARG
import com.devi3east.bashbuddy.cart.navigation.STRIPE_SESSION_ID_ARG
import com.devi3east.bashbuddy.di.initKoinJs
import com.devi3east.bashbuddy.features.app.data.Screen
import com.devi3east.bashbuddy.features.events.detail.navigation.EVENT_ID_ARG
import com.devi3east.bashbuddy.features.events.detail.navigation.EVENT_SLUG_ARG
import com.devi3east.bashbuddy.features.route.BrowserRoute
import com.devi3east.bashbuddy.supabase.SupabaseClient
import com.devi3east.bashbuddy.ticketDetail.navigation.TICKET_ID_ARG
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.skiko.wasm.onWasmReady
import org.w3c.dom.url.URLSearchParams

@OptIn(ExperimentalComposeUiApi::class)
fun main() {
    initKoinJs()
    SupabaseClient.initialize()

    onWasmReady {
        ComposeViewport(document.body!!) {
            val navController = rememberNavController()

            var lastPath by remember {
                mutableStateOf<String?>(null)
            }

            // --------------------------------------------------
            // Browser Back / Forward → App
            // --------------------------------------------------

            LaunchedEffect(Unit) {
                window.onpopstate = {
                    val route = parseBrowserRoute()

                    val targetRoute = when (route) {
                        BrowserRoute.Home ->
                            Screen.EventsScreen.route

                        BrowserRoute.SignUp ->
                            Screen.SignUpScreen.route

                        BrowserRoute.SignIn ->
                            Screen.SignInScreen.route

                        BrowserRoute.Leaderboard ->
                            Screen.LeaderboardScreen.route

                        BrowserRoute.Cart ->
                            Screen.CartScreen.route

                        BrowserRoute.TermsOfService ->
                            Screen.TermsOfServiceScreen.route

                        BrowserRoute.PrivacyPolicy ->
                            Screen.PrivacyPolicyScreen.route

                        BrowserRoute.ContactUs ->
                            Screen.ContactUsScreen.route

                        BrowserRoute.Dashboard ->
                            Screen.DashboardScreen.route

                        BrowserRoute.Profile ->
                            Screen.ProfileScreen.route

                        BrowserRoute.Organization ->
                            Screen.OrganizationScreen.route

                        BrowserRoute.Favorites ->
                            Screen.FavoriteEventsScreen.route

                        BrowserRoute.Search ->
                            Screen.SearchScreen.route

                        is BrowserRoute.TicketDetail ->
                            Screen.TicketDetailScreen.createRoute(
                                route.ticketId
                            )

                        is BrowserRoute.PrizeList ->
                            Screen.PrizeListScreen.createRoute(
                                route.eventId,
                                route.slug
                            )

                        is BrowserRoute.EventDetail ->
                            Screen.EventDetailScreen.createRoute(
                                route.eventId,
                                route.slug
                            )

                        is BrowserRoute.StripeSuccess ->
                            Screen.StripeSuccessScreen.createRoute(
                                route.sessionId,
                                route.orgId
                            )
                    }

                    navController.navigate(targetRoute) {
                        launchSingleTop = true
                    }
                }
            }

            // --------------------------------------------------
            // Browser → App Initial Route
            // --------------------------------------------------

            val startDestination = remember {
                when (val browserRoute = parseBrowserRoute()) {
                    BrowserRoute.Home ->
                        Screen.EventsScreen.route

                    BrowserRoute.SignUp ->
                        Screen.SignUpScreen.route

                    BrowserRoute.SignIn ->
                        Screen.SignInScreen.route

                    BrowserRoute.Leaderboard ->
                        Screen.LeaderboardScreen.route

                    BrowserRoute.Cart ->
                        Screen.CartScreen.route

                    BrowserRoute.TermsOfService ->
                        Screen.TermsOfServiceScreen.route

                    BrowserRoute.PrivacyPolicy ->
                        Screen.PrivacyPolicyScreen.route

                    BrowserRoute.ContactUs ->
                        Screen.ContactUsScreen.route

                    BrowserRoute.Dashboard ->
                        Screen.DashboardScreen.route

                    BrowserRoute.Profile ->
                        Screen.ProfileScreen.route

                    BrowserRoute.Organization ->
                        Screen.OrganizationScreen.route

                    BrowserRoute.Favorites ->
                        Screen.FavoriteEventsScreen.route

                    BrowserRoute.Search ->
                        Screen.SearchScreen.route

                    is BrowserRoute.TicketDetail ->
                        Screen.TicketDetailScreen.createRoute(
                            browserRoute.ticketId
                        )

                    is BrowserRoute.PrizeList ->
                        Screen.PrizeListScreen.createRoute(
                            browserRoute.eventId,
                            browserRoute.slug
                        )

                    is BrowserRoute.EventDetail ->
                        Screen.EventDetailScreen.createRoute(
                            browserRoute.eventId,
                            browserRoute.slug
                        )

                    is BrowserRoute.StripeSuccess ->
                        Screen.StripeSuccessScreen.createRoute(
                            browserRoute.sessionId,
                            browserRoute.orgId
                        )
                }
            }

            // --------------------------------------------------
            // Render App
            // --------------------------------------------------

            App(
                navController = navController,
                startDestination = startDestination
            )

            // --------------------------------------------------
            // App → Browser URL Sync
            // --------------------------------------------------

            LaunchedEffect(navController) {
                navController.currentBackStackEntryFlow.collect { entry ->
                    val route =
                        entry.destination.route
                            ?: return@collect

                    val path = when {
                        route.startsWith(Screen.SignUpScreen.route) ->
                            "/signup"

                        route.startsWith(Screen.SignInScreen.route) ->
                            "/signin"

                        route.startsWith(Screen.LeaderboardScreen.route) ->
                            "/leaderboard"

                        route.startsWith(Screen.TermsOfServiceScreen.route) ->
                            "/terms"

                        route.startsWith(Screen.PrivacyPolicyScreen.route) ->
                            "/privacy"

                        route.startsWith(Screen.ContactUsScreen.route) ->
                            "/contact"

                        route.startsWith(Screen.DashboardScreen.route) ->
                            "/account/dashboard"

                        route.startsWith(Screen.ProfileScreen.route) ->
                            "/account/profile"

                        route.startsWith(Screen.OrganizationScreen.route) ->
                            "/account/organization"

                        route.startsWith(Screen.SearchScreen.route) ->
                            "/search"

                        route.startsWith(Screen.FavoriteEventsScreen.route) ->
                            "/favorites"

                        route.startsWith("event/") &&
                                !route.contains("/prizelist") -> {
                            val args =
                                entry.arguments
                                    ?: return@collect

                            val eventId =
                                args.read {
                                    getString(EVENT_ID_ARG)
                                }

                            val slug =
                                args.read {
                                    getString(EVENT_SLUG_ARG)
                                }

                            "/event/$eventId/$slug"
                        }

                        route.startsWith("event/") &&
                                route.contains("/prizelist") -> {
                            val args =
                                entry.arguments
                                    ?: return@collect

                            val eventId =
                                args.read {
                                    getString(EVENT_ID_ARG)
                                }

                            val slug =
                                args.read {
                                    getString(EVENT_SLUG_ARG)
                                }

                            "/event/$eventId/$slug/prizelist"
                        }

                        route.startsWith("ticket/") -> {
                            val args =
                                entry.arguments
                                    ?: return@collect

                            val ticketId =
                                args.read {
                                    getString(TICKET_ID_ARG)
                                }

                            "/ticket/$ticketId"
                        }

                        route.startsWith(
                            Screen.StripeSuccessScreen.route
                                .substringBefore("/")
                        ) -> {
                            val args =
                                entry.arguments
                                    ?: return@collect

                            val sessionId =
                                args.read {
                                    getString(
                                        STRIPE_SESSION_ID_ARG
                                    )
                                }

                            val orgId =
                                args.read {
                                    getString(
                                        STRIPE_ORG_ID_ARG
                                    )
                                }

                            "/stripe/success" +
                                    "?session_id=$sessionId" +
                                    "&org_id=$orgId"
                        }

                        route.startsWith(
                            Screen.EventsScreen.route
                        ) ->
                            "/"

                        else ->
                            null
                    }

                    path?.let { newPath ->
                        if (
                            newPath.startsWith("/") &&
                            newPath != lastPath
                        ) {
                            window.history.pushState(
                                null,
                                "",
                                newPath
                            )

                            lastPath = newPath
                        }
                    }
                }
            }
        }
    }
}

/* ------------------------------------------------------
   Browser → App route parsing
   ------------------------------------------------------ */

fun parseBrowserRoute(): BrowserRoute {
    val path =
        window.location.pathname.trim('/')

    return when {
        path == "signup" ->
            BrowserRoute.SignUp

        path == "signin" ->
            BrowserRoute.SignIn

        path == "terms" ->
            BrowserRoute.TermsOfService

        path == "privacy" ->
            BrowserRoute.PrivacyPolicy

        path == "contact" ->
            BrowserRoute.ContactUs

        path == "leaderboard" ->
            BrowserRoute.Leaderboard

        path == "account/dashboard" ->
            BrowserRoute.Dashboard

        path == "account/profile" ->
            BrowserRoute.Profile

        path == "account/organization" ->
            BrowserRoute.Organization

        path == "search" ->
            BrowserRoute.Search

        path == "favorites" ->
            BrowserRoute.Favorites

        path.startsWith("ticket/") -> {
            val parts =
                path.split('/')
                    .filter {
                        it.isNotBlank()
                    }

            if (parts.size == 2) {
                BrowserRoute.TicketDetail(
                    parts[1]
                )
            } else {
                BrowserRoute.Home
            }
        }

        path.startsWith("event/") -> {
            val parts =
                path.split('/')
                    .filter {
                        it.isNotBlank()
                    }

            when {
                // /event/{id}/{slug}

                parts.size == 3 ->
                    BrowserRoute.EventDetail(
                        parts[1],
                        parts[2]
                    )

                // /event/{id}/{slug}/prizelist

                parts.size == 4 &&
                        parts[3] == "prizelist" ->
                    BrowserRoute.PrizeList(
                        parts[1],
                        parts[2]
                    )

                else ->
                    BrowserRoute.Home
            }
        }

        path == "stripe/success" -> {
            val params =
                URLSearchParams(
                    window.location.search
                )

            val sessionId =
                params.get("session_id")

            val orgId =
                params.get("org_id")

            if (
                sessionId != null &&
                orgId != null
            ) {
                BrowserRoute.StripeSuccess(
                    sessionId,
                    orgId
                )
            } else {
                BrowserRoute.Home
            }
        }

        path == "stripe/cancel" ->
            BrowserRoute.Cart

        else ->
            BrowserRoute.Home
    }
}